import { useState } from "react";
import { Grid, Box, Typography, Button, TextField, IconButton, Checkbox, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";
import { addProductImages, updateProductImages } from "../../../../../services/Admin/ProductsMediaAndDetailsApi";
import { deleteProductImages } from "../../../../../services/Admin/ProductsMediaAndDetailsApi";

const ProductImagesTab = ({ images = [], productId, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [titleAr, setTitleAr] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editTitleAr, setEditTitleAr] = useState("");
  const [editTitleEn, setEditTitleEn] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);

  // NEW: multi-select / delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
  };

  const handleUpload = async () => {
    setError(null);
    if (!productId) return setError("productId is required");
    if (!file) return setError("Please select an image file");
    setLoading(true);
    try {
      const formData = new FormData();
      // keys expected by the backend as shown in your screenshot
      formData.append("images[0][image]", file);
      formData.append("images[0][title_ar]", titleAr);
      formData.append("images[0][title_en]", titleEn);

      // keep a textual representation for debugging / display
      setLastPayload({
        "images[0][image]": file.name,
        "images[0][title_ar]": titleAr,
        "images[0][title_en]": titleEn,
      });

      await addProductImages(productId, formData);
      // notify parent to refresh product data (images list)
      if (typeof onUploaded === "function") onUploaded();
      // clear inputs
      setFile(null);
      setTitleAr("");
      setTitleEn("");
    } catch (err) {
      setError(err?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (im) => {
    setEditingId(im.id);
    setEditTitleAr(im.title_ar || "");
    setEditTitleEn(im.title_en || "");
    setEditFile(null);
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditFile(null);
    setEditTitleAr("");
    setEditTitleEn("");
    setEditError(null);
  };

  const handleEditFileChange = (e) => {
    setEditFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    setEditError(null);
    setEditLoading(true);
    try {
      const fd = new FormData();
      fd.append("images[0][id]", editingId);
      if (editFile) fd.append("images[0][image]", editFile);
      fd.append("images[0][title_ar]", editTitleAr);
      fd.append("images[0][title_en]", editTitleEn);

      await updateProductImages(fd);
      if (typeof onUploaded === "function") onUploaded();
      cancelEdit();
    } catch (err) {
      setEditError(err?.message || "Update failed");
    } finally {
      setEditLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearSelection = () => setSelectedIds([]);

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return;
    const ok = window.confirm(`حذف ${selectedIds.length} صورة؟`);
    if (!ok) return;
    setDeleteError(null);
    setDeleteLoading(true);
    try {
      await deleteProductImages(selectedIds);
      // notify parent to refresh
      if (typeof onUploaded === "function") onUploaded();
      clearSelection();
    } catch (err) {
      setDeleteError(err?.message || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>صور</Typography>

      {/* Multi-delete toolbar */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleDeleteSelected}
          disabled={selectedIds.length === 0 || deleteLoading}
        >
          {deleteLoading ? "حذف..." : `حذف المحدد (${selectedIds.length})`}
        </Button>
        {deleteError && <Typography color="error" variant="body2">{deleteError}</Typography>}
      </Box>

      <Grid container spacing={1} sx={{ mb: 2 }}>
        {images.map((im) => (
          <Grid item key={im.id}>
            <Box sx={{ position: "relative", width: 140, height: 140, bgcolor: "#f5f5f5", borderRadius: 1, overflow: "hidden" }}>
              <img src={getImageUrl(im.image)} alt={im.title_ar || im.title_en} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <Box sx={{ position: "absolute", top: 4, right: 4 }}>
                <Tooltip title="اختر للحذف">
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(im.id)}
                    onChange={() => toggleSelect(im.id)}
                    sx={{ bgcolor: "rgba(255,255,255,0.8)", borderRadius: 1 }}
                  />
                </Tooltip>
              </Box>
            </Box>

            {/* display metadata and edit UI */}
            {editingId === im.id ? (
              <Box sx={{ mt: 0.5, maxWidth: 180 }}>
                <TextField fullWidth size="small" label="العنوان بالعربي" value={editTitleAr} onChange={(e) => setEditTitleAr(e.target.value)} sx={{ mb: 0.5 }} />
                <TextField fullWidth size="small" label="العنوان بالإنجليزي" value={editTitleEn} onChange={(e) => setEditTitleEn(e.target.value)} sx={{ mb: 0.5 }} />
                <input type="file" accept="image/*" onChange={handleEditFileChange} style={{ display: "block", marginBottom: 8 }} />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveEdit} disabled={editLoading}>
                    {editLoading ? "جاري الحفظ..." : "حفظ"}
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<CloseIcon />} onClick={cancelEdit} disabled={editLoading}>
                    إلغاء
                  </Button>
                </Box>
                {editError && <Typography color="error" variant="caption" display="block" sx={{ mt: 0.5 }}>{editError}</Typography>}
              </Box>
            ) : (
              <Box sx={{ mt: 0.5, maxWidth: 140 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                  {im.title_ar || im.title_en || "—"}
                </Typography>
                {im.title_en && im.title_ar && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                    {im.title_en}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {formatDate(im.created_at)}
                </Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                  <IconButton size="small" onClick={() => startEdit(im)} aria-label="edit">
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Grid>
        ))}
      </Grid>

      {/* ...existing upload UI for adding new images ... */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
        <input id="product-image-input" type="file" accept="image/*" onChange={handleFileChange} style={{ display: "inline-block" }} />
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
        <TextField label="العنوان بالعربي" value={titleAr} onChange={(e) => setTitleAr(e.target.value)} size="small" />
        <TextField label="العنوان بالإنجليزي" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} size="small" />
        <Button variant="contained" onClick={handleUpload} disabled={loading}>
          {loading ? "جاري الرفع..." : "إضافة صورة"}
        </Button>
      </Box>

      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {/* Show payload that will be sent (debug friendly) */}
      {lastPayload && (
        <Box sx={{ mt: 1, p: 1, bgcolor: "#f7f7f7", borderRadius: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>البيانات المرسلة:</Typography>
          <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(lastPayload, null, 2)}</pre>
        </Box>
      )}
    </Box>
  );
};

export default ProductImagesTab;
